<script setup>
import { ref, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    class: {
        type: String,
        default: ''
    },
    options: {
        type: Array,
        default: () => [
            { name: 'Inbox', value: 'inbox' },
            { name: 'General', value: 'general' },
            { name: 'Archived', value: 'archived' }
        ]
    },
    data: {
        type: Object,
        required: true
    },
    title: {
        type: String,
        default: 'Notifications'
    },
    markAllAsReadText: {
        type: String,
        default: 'Mark all as read'
    },
    onMarkAllAsRead: {
        type: Function,
        default: () => {}
    }
});

const selectedOption = ref(props.options[0].value);

// Handle mark all as read
const handleMarkAllAsRead = () => {
    props.onMarkAllAsRead();
};
</script>

<template>
    <div
        :class="props.class"
        class="rounded-2xl bg-surface-0 dark:bg-surface-950 shadow-[0px_129.205px_36.493px_0px_rgba(44,54,87,0.00),_0px_82.849px_33.534px_0px_rgba(44,54,87,0.01),_0px_46.356px_27.616px_0px_rgba(44,54,87,0.03),_0px_20.712px_20.712px_0px_rgba(44,54,87,0.05),_0px_4.932px_11.836px_0px_rgba(44,54,87,0.06)] dark:shadow-stroke"
    >
        <div class="p-4 pl-6 flex items-center justify-between border-b">
            <span class="font-medium">{{ $t(title) }}</span>
            <button 
                @click="handleMarkAllAsRead"
                class="px-2 py-1 text-sm rounded-lg border hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors duration-150"
            >
                {{ $t(markAllAsReadText) }}
            </button>
        </div>
        <div class="p-2 space-y-4">
            <SelectButton 
                v-model="selectedOption" 
                :options="options" 
                option-label="name" 
                option-value="value" 
                pt:root:class="!w-full [&_button]:!flex-1" 
            />
            <template v-for="item in data[selectedOption]" :key="item.id">
                <div class="p-3 flex items-start gap-3 rounded-lg cursor-pointer hover:bg-surface-100 dark:hover:bg-surface-900 transition-colors duration-150">
                    <Avatar :image="item.avatar" class="!w-10 !h-10 !rounded-md !overflow-hidden" />
                    <div class="flex-1 mt-0.5">
                        <div class="">
                            <div class="flex items-center justify-between gap-2">
                                <span>{{ item.name }}</span>
                                <span class="text-sm text-surface-500">{{ item.date }}</span>
                            </div>
                            <p class="mt-0.5 line-clamp-1 text-sm text-surface-500">
                                {{ item.message }}
                            </p>
                        </div>
                        <div v-if="item?.action === 'confirmation'" class="mt-1 flex items-center gap-3">
                            <button class="flex-1 landing-button-secondary">{{ $t('Cancel') }}</button>
                            <button class="flex-1 landing-button-primary">{{ $t('Confirm') }}</button>
                        </div>
                        <div v-if="item?.action === 'warn'" class="mt-1 flex items-start gap-2 p-2 pl-3 rounded-xl" :class="item.action === 'warn' ? 'bg-orange-500/10 [&_p]:text-orange-600 [&_i]:text-orange-600' : ''">
                            <i class="pi pi-exclamation-triangle mt-1" />
                            <p class="text-sm text-surface-500">{{ item.actionText }}</p>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>